import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a chunk load error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Unable to preload CSS') ||
      error.message.includes('error loading dynamically imported module');
    
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChunkErrorBoundary caught error:', error, errorInfo);
    
    // If it's a chunk error, try to auto-refresh once
    if (this.state.isChunkError) {
      const hasRefreshed = sessionStorage.getItem('chunk_error_refreshed');
      
      if (!hasRefreshed) {
        // Mark that we've tried refreshing
        sessionStorage.setItem('chunk_error_refreshed', 'true');
        // Clear the flag after 30 seconds (in case user manually refreshes later)
        setTimeout(() => {
          sessionStorage.removeItem('chunk_error_refreshed');
        }, 30000);
        // Refresh the page to get new chunks
        window.location.reload();
      }
    }
  }

  handleRefresh = () => {
    sessionStorage.removeItem('chunk_error_refreshed');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-surface-bg">
            <div className="text-center p-8 max-w-md">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h2 className="font-display text-xl font-semibold text-text-primary mb-2">
                App Updated
              </h2>
              <p className="text-text-body mb-6">
                A new version of SyncEHS is available. Please refresh to continue.
              </p>
              <button
                onClick={this.handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue"
              >
                Refresh Now
              </button>
            </div>
          </div>
        );
      }
      
      // Generic error fallback
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-bg">
          <div className="text-center p-8 max-w-md">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-status-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary mb-2">
              Something went wrong
            </h2>
            <p className="text-text-body mb-6">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            <button
              onClick={this.handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
